import { EMPTY, firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import { catchError, timeout } from 'rxjs/operators';

import {
  PaymentFinalizeInterface,
  PaymentTransportationMethod,
  PaymentTokenUrl,
  PaginationResult,
  PaymentEntity,
  PaymentCreateDto,
  Order,
  OrderInfo,
  getMainFileOrNothing,
  PaymentErrorType,
  PaymentError,
} from 'src/app/shared';

import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import {
  AppServiceEvent,
  ConfigService,
  HTTP_REQUESTS_TIMEOUT,
  IS_SERVER_PLATFORM,
  PaymentStatus,
} from 'src/app/core';
import { TransportationMethodService } from './transportation-method.service';
import { PaymentTransaction } from 'src/app/shared/interfaces/payment/payment-transaction';
import { ReferralService } from 'src/app/core/services/referral.service';
import { IPaymentFinalize } from '../../basket/interfaces/IPaymentFinalize';

export interface PaymentServiceEvent
  extends AppServiceEvent<'update' | 'create' | 'delete', PaymentEntity> {}

@Injectable({ providedIn: 'root' })
export class PaymentService {
  constructor(
    private transportationService: TransportationMethodService,
    private referralService: ReferralService,
    private configService: ConfigService,
    private http: HttpClient,
    @Inject(IS_SERVER_PLATFORM) private isServer: boolean
  ) {}

  /**
   * Get one payment using its very own ID
   */

  public findOne(id: number) {
    const url = this.configService.getUrl('payment.');
  }

  /**
   * Gets user payments items from database
   * @returns {PaginationResult<PaymentEntity>}
   */
  public getUserPayments(): Observable<PaginationResult<PaymentEntity>> {
    const url = this.configService.getUrl('user.selfPayments');
    return this.http
      .get<PaginationResult<PaymentEntity>>(url, {
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  /**
   * Get every finalized and done payment objects
   * to show in admin panel
   */
  public loadPaymentForUser(
    userId: number,
    filters: {
      page?: number;
      limit?: number;
      search?: number;
    }
  ) {
    const url = this.configService
      .getUrl('user.payments')
      .replace(':id', userId.toString());

    const params = this.getMergedQueryParam({
      ...filters,
    });

    return this.http
      .get<PaginationResult<PaymentEntity>>(url, {
        params,
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public create(createDto: PaymentCreateDto): Observable<PaymentEntity> {
    const url = this.configService.getUrl('payment.createPayment') as string;

    return this.http
      .post<PaymentEntity>(url, createDto, { withCredentials: true })
      .pipe(catchError((err) => this.errorHandler(err)));
  }

  public readOne(id: number): Observable<PaymentEntity> {
    const url = this.configService
      .getUrl('payment.readOne')
      .replace(':id', `${id}`);

    return this.http.get<PaymentEntity>(url, { withCredentials: true }).pipe(
      catchError((err) => this.errorHandler(err)),
      timeout(HTTP_REQUESTS_TIMEOUT)
    );
  }

  /**
   * This method standardized to create transaction
   * object and opens the transaction on Zarinpal.
   * then returns the url to redirect user for payment
   * everything is done automatically after the transaction
   * is returned.
   */
  public startPaymentAndRedirect(paymentId: number) {
    const url = this.configService
      .getUrl('payment.start:zarinpal')
      .replace(':id', paymentId.toString());

    this.http.get<{ url: string }>(url, { withCredentials: true }).subscribe({
      next: (result: { url: string }) => {
        if (!this.isServer) document.location.href = result.url;
      },
    });
  }

  // public markPaymentAsDone(id: number) {
  //   const url = this.configService
  //     .getUrl('payment.markAsDone')
  //     .replace(':id', `${id}`);

  //   return this.http.get(url, { withCredentials: true });
  // }

  public getOrdersInfo(orders: Order[]) {
    return orders;
  }

  public readByHash(hash: number): Observable<PaymentEntity> {
    const url = this.configService
      .getUrl('payment.getPaymentByHash')
      .replace(':hash', `${hash}`);

    return this.http
      .get<PaymentEntity>(url, { withCredentials: true })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT), timeout(HTTP_REQUESTS_TIMEOUT));
  }

  public readAll(options: {
    [key: string]: string | number;
  }): Observable<PaginationResult<PaymentEntity>> {
    const url = this.configService.getUrl('payment.index');
    const params = this.getMergedQueryParam(options);

    return this.http
      .get<PaginationResult<PaymentEntity>>(url, {
        params,
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  /**
   * Send data and get url.
   */
  public finalizePayment(
    id: number,
    finalizeObject: PaymentFinalizeInterface
  ): Observable<PaymentEntity> {
    const url = this.getFinalizeUrl(id);
    const referral = this.referralService.readFromLocalStorage();

    if (referral && referral.id) {
      finalizeObject.referralId = referral.id;
    }

    return this.http
      .post<PaymentEntity>(url, finalizeObject, {
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  /**
   * This is first step of paying the payment object.
   * We send request to API and fetch url.
   *
   * @param id Payment object ID.
   */
  public getPaymentToken(id: number): Observable<PaymentTokenUrl> {
    const url = this.getUrlRequestUrl(id);
    return this.http
      .get<PaymentTokenUrl>(url, {
        withCredentials: true,
      })
      .pipe(timeout(HTTP_REQUESTS_TIMEOUT));
  }

  /**
   * Because we want to change status in a flow
   * and we need to get next allowed phase.
   * @param {PaymentStatus} currentStatus
   * @returns { PaymentStatus } Next status.
   */
  public getPaymentNextStatus(currentStatus?: PaymentStatus): PaymentStatus {
    switch (currentStatus) {
      case PaymentStatus.NotDone:
        return PaymentStatus.Implementation;

      case PaymentStatus.Done:
        return PaymentStatus.Sent;

      case PaymentStatus.Implementation:
        return PaymentStatus.Done;

      case PaymentStatus.Sent:
        return PaymentStatus.Sent;

      case PaymentStatus.Canceled:
        return PaymentStatus.Canceled;

      case PaymentStatus.Payment:
        return PaymentStatus.Payment;

      default:
        throw new Error('Payment status not defined');
    }
  }

  /**
   * Get persian word of payment status
   * @param {PaymentStatus} status
   */
  public getPersianStatusForPayment(status?: PaymentStatus): string {
    switch (status) {
      case PaymentStatus.Done:
        return 'انجام شده';

      case PaymentStatus.Canceled:
        return 'لغو شده';

      case PaymentStatus.Implementation:
        return 'درحال انجام';

      case PaymentStatus.Payment:
        return 'در انتظار پرداخت';

      case PaymentStatus.NotDone:
        return 'انجام نشده';

      case PaymentStatus.Sent:
        return 'ارسال شده';

      default:
        throw new Error(
          'Status switch not defined in getting persian word: ' + status
        );
    }
  }

  /**
   * Update status of payment with admin or manager privilege
   * @param status
   * @returns
   */
  public async updatePaymentStatus(
    id: number,
    status: PaymentStatus
  ): Promise<PaymentEntity> {
    const url = this.configService.getUrl('payment.updateStatus');
    return await firstValueFrom(
      this.http
        .put<PaymentEntity>(url, { id, status }, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
    );
  }

  /**
   * This method adds or overwrites the post tracking
   * code on server and returns payment object
   * @return {Payment} Updated payment object.
   */
  public async overwritePaymentPostTrackingCode(
    id: number,
    code: string
  ): Promise<PaymentEntity> {
    const url = this.configService.getUrl('payment.updatePostTrackingCode');
    return firstValueFrom(
      this.http
        .put<PaymentEntity>(url, { id, code }, { withCredentials: true })
        .pipe(timeout(HTTP_REQUESTS_TIMEOUT))
    );
  }

  /**
   * ## Read transactions from server
   * @param {number} userId if you pass it down you get only user's transactions
   */
  public getTransactions(
    userId?: number,
    params?: { [key: string]: string | number }
  ): Observable<PaginationResult<PaymentTransaction>> {
    const getTransactionUrl = this.configService.getUrl('payment.transactions');
    const queryParams = this.getMergedQueryParam({
      user: userId,
      ...params,
    });

    return this.http.get<PaginationResult<PaymentTransaction>>(
      getTransactionUrl,
      {
        withCredentials: true,
        params: queryParams,
      }
    );
  }

  public createAndFinalize(dto: IPaymentFinalize) {
    const url = this.configService.getUrl('payment.createAndFinalize');
    return this.http.post<PaymentEntity>(url, dto, { withCredentials: true });
  }

  // -------------------------------------Private methods|

  private getMergedQueryParam(objectOfParams: Record<string, any>): HttpParams {
    for (const key in objectOfParams) {
      if (!!!objectOfParams[key]) {
        delete objectOfParams[key];
      }
    }

    const params = new HttpParams({ fromObject: objectOfParams });
    return params;
  }

  private getFinalizeUrl(id: number) {
    const url = this.configService.getUrl('payment.finalize');
    return url.replace(':id', id.toString()).trim();
  }

  private getUrlRequestUrl(id: number): string {
    const url = this.configService.getUrl('payment.getPayToken');
    return url.replace(':id', id.toString());
  }

  public getTransportationMethods(): Observable<PaymentTransportationMethod[]> {
    return this.transportationService.getAll();
  }

  public cancelPayment(id: number): Observable<PaymentEntity> {
    const url = this.configService
      .getUrl('payment.cancel')
      .replace(':id', `${id}`);

    return this.http.put<PaymentEntity>(url, {});
  }

  // TODO: Go from this way
  public addError(
    id: number,
    type: PaymentErrorType,
    message: string,
    status?: PaymentStatus
  ): Observable<PaymentError> {
    const url = this.configService.getUrl('payment.addError');
    return this.http.post<PaymentError>(
      url,
      { id, type, message, status },
      { withCredentials: true }
    );
  }

  errorHandler(err: any) {
    if (err instanceof HttpErrorResponse) {
      switch (err.status) {
        case 404:
          throw new Error(err.message);

        case 401:
          throw new Error(
            `The request returned <${err.status}> because, user not authorized.`
          );

        case 500:
          throw new Error(
            `The request for payment returned with status<${err.status}>. Server Internal Error`
          );

        default:
          throw err;
      }
    }

    return EMPTY;
  }
}
